import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SideBar } from "../../../components/nav/SideBar";
import { Content, MainContent } from "../../../styles/styles";
import { useNavigationStore } from "../../../store/navigationStore";
import HomeBackground from "../../../components/background/HomeBackground";
import { ReactComponent as QuickIcon1 } from "@/assets/home/quick_icon1.svg?react";
import { ReactComponent as QuickIcon2 } from "@/assets/home/quick_icon2.svg?react";
import { ReactComponent as QuickIcon3 } from "@/assets/home/quick_icon3.svg?react";
import { ReactComponent as QuickIcon4 } from "@/assets/home/quick_icon4.svg?react";
import {
  TableContainer as SharedTableContainer,
  KoTitle1,
  EnHeadline,
  TableContainer
} from "../../../styles/sharedStyles";
import {
  IntroContainer,
  IntroTitleContainer,
  SubText,
  QuickButton,
  IconWrapper,
  ButtonText,
  HistorySection,
  QuickSection,
  QuickBox,
  HoverOverlay,
} from "./styles";
import { getList, getOutputDetail, ListItem } from "../../../apis/dashboard/list";
import { convertTimestampToDate } from "../../../utils/formatTime";
import { useModal } from "store/modalStore";
import { useProcessVuStore } from "@/store/processVuStore";
import { useProcessVhStore } from "@/store/processVhStore";

type ProjectRouteId = "audion_vu" | "audion_vh";

const detectProjectRoute = (item: ListItem): ProjectRouteId | null => {
  const normalize = (value?: string | null) =>
    value?.toString().toLowerCase().trim();

  const componentMatch = item.components
    ?.map((component) => normalize(component))
    .find(
      (component): component is ProjectRouteId =>
        component === "audion_vu" || component === "audion_vh"
    );
  if (componentMatch) return componentMatch;

  const projectId = normalize(item.projectId);
  if (projectId === "audion_vu" || projectId === "audion_vh") {
    return projectId;
  }

  const projectName = normalize(item.projectName);
  if (projectName) {
    if (projectName.includes("highlight") || projectName.includes("하이라이트")) {
      return "audion_vh";
    }
    if (
      projectName.includes("understanding") ||
      projectName.includes("음성이해")
    ) {
      return "audion_vu";
    }
  }

  return null;
};

const WorkspacePage: React.FC = () => {
  const navigate = useNavigate();

  const { isCollapsed } = useNavigationStore();
  const isSubtitleGenerationAvailable = false;

  const { openInfo } = useModal();
  const { setResult: setVuResult } = useProcessVuStore();
  const { setResult: setVhResult } = useProcessVhStore();

  const [recentList, setRecentList] = useState<ListItem[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);

  const handleViewRecentItem = async (item: ListItem) => {
    if (!item.documentId) {
      openInfo({
        title: "이동 불가",
        message: "문서 ID가 없어 결과 페이지로 이동할 수 없습니다.",
      });
      return;
    }

    const projectRoute = detectProjectRoute(item);
    if (!projectRoute) {
      openInfo({
        title: "이동 불가",
        message: "지원되지 않는 프로젝트 유형입니다.",
      });
      return;
    }

    try {
      const response = await getOutputDetail(item.id);
      const result = response.content;

      if (projectRoute === "audion_vu") {
        setVuResult(item.documentId, result as any);
      } else if (projectRoute === "audion_vh") {
        setVhResult(item.documentId, result as any);
      }

      navigate(`/results/${projectRoute}/${item.documentId}`, {
        state: {
          projectName: item.projectName,
          result,
        },
      });
    } catch (error) {
      console.error("Failed to load output detail:", error);
      openInfo({
        title: "오류",
        message: "결과를 불러오는 중 문제가 발생했습니다.",
      });
    }
  };

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setIsLoadingRecent(true);
        setRecentError(null);
        const response = await getList({
          page: 0,
          size: 10,
          status: ["completed", "failed", "running"],
        });
        const list = response.content?.list ?? [];
        setRecentList(list);
      } catch (error) {
        console.error("Failed to fetch recent list:", error);
        setRecentError("최근 작업을 불러오지 못했습니다.");
      } finally {
        setIsLoadingRecent(false);
      }
    };

    void fetchRecent();
  }, []);

  return (
    <HomeBackground showImage={true}>
      <Content $isCollapsed={isCollapsed}>
        <SideBar />
        <MainContent style={{ gap: "60px" }}>
          <IntroContainer>
            <IntroTitleContainer>
              <EnHeadline>
              음성 AI로 새로운 가능성을 만드세요.
              </EnHeadline>
              <SubText>
              오디온의 음성 지능 기술로 당신의 제품을 더 강력하게 발전시킵니다.
              </SubText>
            </IntroTitleContainer>
          </IntroContainer>
          <QuickSection>
            <KoTitle1>새 프로젝트</KoTitle1>
            <QuickBox>
              <QuickButton
                onClick={() => {
                  navigate("/upload/audion_vu");
                }}
              >
                <IconWrapper>
                  <QuickIcon1 />
                </IconWrapper>
                <ButtonText>음성이해</ButtonText>
                <HoverOverlay>화자의 감정과 맥락을 <br /> 함께 이해합니다.</HoverOverlay>
              </QuickButton>
              <QuickButton
                onClick={() => {
                  navigate("/upload/audion_vh");
                }}
              >
                <IconWrapper>
                  <QuickIcon4 />
                </IconWrapper>
                <ButtonText>보이스 하이라이트</ButtonText>
                <HoverOverlay>음성의 핵심 구간을 <br /> 시각화합니다.</HoverOverlay>
              </QuickButton>
              <QuickButton
                disabled={!isSubtitleGenerationAvailable}
                onClick={() => {
                  if (!isSubtitleGenerationAvailable) return;
                  navigate("/upload/audion_sg");
                }}
              >
                <IconWrapper>
                  <QuickIcon3 />
                </IconWrapper>
                <ButtonText>자막 생성 (오픈 예정)</ButtonText>
                <HoverOverlay>서비스 준비 중입니다. <br /> 곧 찾아뵐게요.</HoverOverlay>
              </QuickButton>
              <QuickButton
                onClick={() => {
                  navigate("/realtime_stt");
                }}
              >
                <IconWrapper>
                  <QuickIcon2 />
                </IconWrapper>
                <ButtonText>실시간 음성인식</ButtonText>
                <HoverOverlay>실시간으로 음성을 <br /> 텍스트로 변환합니다.</HoverOverlay>
              </QuickButton>
            </QuickBox>
          </QuickSection>

          <HistorySection>
            <KoTitle1>최근 작업</KoTitle1>
            <SharedTableContainer style={{ marginTop: "12px" }}>
              {isLoadingRecent ? (
                <p style={{ margin: 0, padding: "12px 0", color: "#727272" }}>
                  최근 작업을 불러오는 중입니다...
                </p>
              ) : recentError ? (
                <p style={{ margin: 0, padding: "12px 0", color: "#DC2626" }}>
                  {recentError}
                </p>
              ) : recentList.length === 0 ? (
                <p style={{ margin: 0, padding: "12px 0", color: "#727272" }}>
                  최근 작업 내역이 없습니다.
                </p>
              ) : (
                <ul
                  style={{
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  {recentList.map((item, index) => {
                    const isLast = index === recentList.length - 1;
                    const isCompleted =
                      typeof item.status === "string" &&
                      item.status.toLowerCase().startsWith("completed");
                    return (
                      <li
                        key={item.id}
                        onClick={() => {
                          if (!isCompleted) {
                            openInfo({
                              title: "이동 불가",
                              message:
                                "완료된 작업만 결과 페이지로 이동할 수 있습니다.",
                            });
                            return;
                          }
                          void handleViewRecentItem(item);
                        }}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 0",
                          borderBottom: isLast
                            ? "none"
                            : "1px solid #EBEEF1",
                          cursor: isCompleted ? "pointer" : "default",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#1A1D23",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.title || item.uploadedFileName}
                          </div>
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: "12px",
                              color: "#727272",
                            }}
                          >
                            {convertTimestampToDate(item.createdAt)} ·{" "}
                            {item.projectName || "프로젝트"}
                          </div>
                        </div>
                        <div
                          style={{
                            marginLeft: 16,
                            fontSize: "12px",
                            color: "#727272",
                            textTransform: "capitalize",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.status}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </SharedTableContainer>
          </HistorySection>
        </MainContent>
      </Content>
    </HomeBackground>
  );
};

export default WorkspacePage;
